// src/components/profile/RewardCodeRow.js
import React, { useMemo, useState } from 'react';
import './RewardCodeRow.css';
import ConfirmRevealCodeDrawer from '../popups/ConfirmRevealCodeDrawer';
import ConfirmDeleteRewardCodeDrawer from '../popups/ConfirmDeleteRewardCodeDrawer'; // ✅ NEW

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

// fallback masker (only used if backend still returns raw code)
function maskCode(code) {
  if (!code) return '';
  const last3 = String(code).slice(-3);
  return `•••• •••• •••${last3}`;
}

export default function RewardCodeRow({
  items = [],
  mode = 'owner',
  onSell = () => {},
  onRedeem = async () => null,
  onDelete = async () => null, // ✅ NEW
  onBuy = () => {},
  onRevealed = () => {},
}) {
  const isOwner = mode === 'owner';

  const sorted = useMemo(() => {
    const arr = Array.isArray(items) ? items : [];
    return [...arr].sort(
      (a, b) =>
        new Date(b.obtainedAt || b.createdAt || 0) -
        new Date(a.obtainedAt || a.createdAt || 0)
    );
  }, [items]);

  // Modal state
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [redeemItem, setRedeemItem] = useState(null);

  // Revealed full code (from secure reveal endpoint)
  const [revealedCode, setRevealedCode] = useState('');

  const [copyMsg, setCopyMsg] = useState('');
  const [redeemBusy, setRedeemBusy] = useState(false);
  const [redeemErr, setRedeemErr] = useState('');

  // ✅ Local UI security lock: once revealed, selling/buying must be disabled immediately
  const [revealedIds, setRevealedIds] = useState(() => new Set());

  // ✅ confirm drawer state (to prevent accidental first reveal)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingItem, setPendingItem] = useState(null);

  // ✅ NEW: delete confirm drawer state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  if (!sorted.length) return null;

  const markRevealedLocal = (id) => {
    if (!id) return;
    setRevealedIds((prev) => {
      const next = new Set(prev);
      next.add(String(id));
      return next;
    });
  };

  const openRedeem = async (c) => {
    setRedeemItem(c);
    setRevealedCode('');
    setRedeemErr('');
    setCopyMsg('');
    setRedeemOpen(true);

    if (!isOwner) return;

    const id = String(c?._id || c?.id || '').trim();
    if (!id) {
      setRedeemErr('Invalid code id');
      return;
    }

    try {
      setRedeemBusy(true);

      // ✅ Call secure reveal/view endpoint via parent
      const out = await onRedeem(c);

      // ✅ accept either code or fullCode (backend safety)
      const code =
        out?.fullCode ||
        out?.code ||
        out?.data?.fullCode ||
        out?.data?.code ||
        out?.payload?.fullCode ||
        out?.payload?.code ||
        '';

      if (!code) {
        setRedeemErr('Failed to reveal code');
        return;
      }

      setRevealedCode(code);

      // ✅ Immediately lock selling locally (no waiting for refresh)
      markRevealedLocal(id);

      // optional: let parent refresh / update list
      try {
        onRevealed({
          id,
          revealedAt: out?.revealedAt || out?.data?.revealedAt || null,
          redeemedAt: out?.redeemedAt || out?.data?.redeemedAt || null,
        });
      } catch (_) {}
    } catch (e) {
      setRedeemErr(
        e?.response?.data?.error || e?.message || 'Failed to reveal code'
      );
    } finally {
      setRedeemBusy(false);
    }
  };

  const closeRedeem = () => {
    setRedeemOpen(false);
    setRedeemItem(null);
    setRevealedCode('');
    setRedeemErr('');
    setCopyMsg('');
    setRedeemBusy(false);
  };

  // ✅ open confirm drawer for first-time reveal only
  const requestReveal = (c, isRevealed) => {
    if (!isOwner) return;

    // If already revealed, open immediately (no confirmation)
    if (isRevealed) {
      openRedeem(c);
      return;
    }

    // First reveal → confirm
    setPendingItem(c);
    setConfirmOpen(true);
  };

  const confirmReveal = async () => {
    const c = pendingItem;
    setConfirmOpen(false);
    setPendingItem(null);
    if (!c) return;
    await openRedeem(c);
  };

  const handleCopy = async () => {
    const code = revealedCode || '';
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
      setCopyMsg('Copied ✅');
      setTimeout(() => setCopyMsg(''), 1500);
    } catch (e) {
      try {
        const ta = document.createElement('textarea');
        ta.value = code;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopyMsg('Copied ✅');
        setTimeout(() => setCopyMsg(''), 1500);
      } catch {
        setCopyMsg('Copy failed');
        setTimeout(() => setCopyMsg(''), 1500);
      }
    }
  };

  // ✅ Confirm delete (soft-hide)
  const confirmDelete = async () => {
    if (!deleteItem) return;

    try {
      setDeleteBusy(true);
      await onDelete(deleteItem);
      setDeleteOpen(false);
      setDeleteItem(null);
    } catch (e) {
      alert(e?.response?.data?.error || e?.message || 'Delete failed');
    } finally {
      setDeleteBusy(false);
    }
  };

  const openDeleteDrawer = (c) => {
    setDeleteItem(c || null);
    setDeleteOpen(true);
  };

  return (
    <>
      <div className="rcode-wrap">
        <div className="rcode-row">
          {sorted.map((c) => {
            const id = String(c._id || c.id || '').trim();

            const cycle =
              c.cycleIdFormatted || String(c.cycleId || '').padStart(5, '0');
            const obtained = fmtDate(c.obtainedAt || c.createdAt);
            const badge = c.badgeLabel || c.badge || 'Badge';

            const statusRaw = String(c.status || '').toUpperCase(); // ACTIVE | REVEALED | REDEEMED...

            const showDelete =
              isOwner && ['REVEALED', 'DEEMED', 'REDEEMED'].includes(statusRaw); // (keeps safe even if typo slips)

            const isActive = statusRaw === 'ACTIVE';

            // ✅ revealed can come from backend OR local after successful reveal
            const isRevealed =
              Boolean(c.isRevealed) ||
              revealedIds.has(id) ||
              statusRaw === 'REVEALED' ||
              statusRaw === 'REDEEMED';

            // ✅ list display uses backend masked field
            const listCode =
              c.codeMasked ||
              maskCode(c.code) || // backward compat only
              (c.codeLast3 ? `•••• •••• •••${c.codeLast3}` : '');

            const displayCode = listCode || '—';

            // Base flags (from backend if present)
            const baseCanSell = Boolean(c.canSell ?? isActive);
            const baseCanBuy = Boolean(c.canBuy ?? c.isForSale);

            // ✅ Owner can VIEW in ACTIVE/REVEALED/REDEEMED
            const canView =
              isOwner && ['ACTIVE', 'REVEALED', 'REDEEMED'].includes(statusRaw);

            // ✅ enforce security locally
            const canSell = isOwner && baseCanSell && isActive && !isRevealed; // kill after reveal
            const canBuy = !isOwner && baseCanBuy && isActive && !isRevealed; // kill after reveal
            const showBuy = !isOwner && canBuy; // ✅ hide buy entirely if not eligible

            // Show status line for anything non-active OR revealed
            const showStatus =
              (statusRaw && statusRaw !== 'ACTIVE') || isRevealed;

            const statusText =
              isRevealed && statusRaw === 'ACTIVE'
                ? 'REVEALED'
                : statusRaw || (isRevealed ? 'REVEALED' : '');

            return (
              <div
                key={id || `${cycle}-${badge}-${obtained}`}
                className="rcode-card"
              >
                <div className="rcode-head">
                  <div className="rcode-title">Reward Code</div>
                  <div className="rcode-pill">#{cycle}</div>
                </div>

                <div className="rcode-meta">
                  <div className="rcode-line">
                    <span className="k">Obtained</span>
                    <span className="v">{obtained || '—'}</span>
                  </div>

                  <div className="rcode-line">
                    <span className="k">Badge</span>
                    <span className="v">{badge}</span>
                  </div>

                  <div className="rcode-line">
                    <span className="k">Code</span>
                    <span className="v rcode-code">{displayCode}</span>
                  </div>

                  {showStatus && (
                    <div className="rcode-line">
                      <span className="k">Status</span>
                      <span className="v">{statusText || '—'}</span>
                    </div>
                  )}
                </div>

                <div className="rcode-actions">
                  {isOwner ? (
                    <>
                      {canSell && (
                        <button className="rcode-btn" onClick={() => onSell(c)}>
                          Sell
                        </button>
                      )}

                      <button
                        className="rcode-btn primary"
                        disabled={!canView}
                        onClick={() => requestReveal(c, isRevealed)}
                      >
                        View
                      </button>

                      {showDelete && (
                        <button
                          className="rcode-btn danger"
                          onClick={() => openDeleteDrawer(c)}
                          disabled={deleteBusy}
                        >
                          Delete
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      {showBuy && (
                        <button
                          className="rcode-btn primary"
                          onClick={() => onBuy(c)}
                        >
                          Buy
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ✅ Confirmation drawer before FIRST reveal */}
      <ConfirmRevealCodeDrawer
        open={confirmOpen}
        onCancel={() => {
          setConfirmOpen(false);
          setPendingItem(null);
        }}
        onConfirm={confirmReveal}
      />

      {/* ✅ NEW: Delete confirmation drawer */}
      <ConfirmDeleteRewardCodeDrawer
        open={deleteOpen}
        onCancel={() => {
          if (deleteBusy) return;
          setDeleteOpen(false);
          setDeleteItem(null);
        }}
        onConfirm={confirmDelete}
        busy={deleteBusy}
        title="Remove reward code?"
        message="Are you sure you want to remove this code? This action cannot be undone. Make sure you have saved or redeemed your code."
      />

      {/* ✅ Owner View Modal */}
      {redeemOpen && isOwner && (
        <div className="rcode-modal-backdrop" onClick={closeRedeem}>
          <div className="rcode-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rcode-modal-title">Your Reward Code</div>
            <div className="rcode-modal-sub">
              Copy this code and paste it in <b>Plans</b> to activate Premium+.
            </div>

            <div className="rcode-modal-code">
              {redeemBusy ? 'Revealing…' : revealedCode || '—'}
            </div>

            {redeemErr && <div className="rcode-modal-error">{redeemErr}</div>}

            <div className="rcode-modal-actions">
              <button className="rcode-btn" onClick={closeRedeem}>
                Close
              </button>

              <button
                className="rcode-btn primary"
                onClick={handleCopy}
                disabled={!revealedCode || redeemBusy}
              >
                Copy
              </button>
            </div>

            {copyMsg && <div className="rcode-modal-toast">{copyMsg}</div>}
          </div>
        </div>
      )}
    </>
  );
}
